import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getTheme } from '../theme';

/**
 * Toast notification system — replaces native alert() with non-blocking in-app toasts.
 *
 * Usage:
 *   const { toasts, addToast, ToastContainer } = useToast();
 *   addToast('Room created!', 'success');
 *   addToast('Failed to join room', 'error');
 *   // Render <ToastContainer /> once at the top level
 */

// ─── Toast Component ───────────────────────────────────────────────────────
const ToastItem = ({ toast, onDismiss, themeKey }) => {
  const [isExiting, setIsExiting] = useState(false);
  const theme = getTheme(themeKey);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsExiting(true);
      setTimeout(() => onDismiss(toast.id), 300);
    }, toast.duration || 4000);

    return () => clearTimeout(timer);
  }, [toast, onDismiss]);

  const handleClick = () => {
    setIsExiting(true);
    setTimeout(() => onDismiss(toast.id), 300);
  };

  const typeStyles = {
    success: {
      background: `linear-gradient(180deg, ${theme.surfaces.card} 0%, ${theme.accents.successSoft} 100%)`,
      border: `1px solid ${theme.accents.successBorder}`,
      iconBackground: theme.accents.success,
      icon: '✓',
    },
    error: {
      background: `linear-gradient(180deg, ${theme.surfaces.card} 0%, ${theme.accents.dangerSoft} 100%)`,
      border: `1px solid ${theme.accents.dangerBorder}`,
      iconBackground: theme.accents.danger,
      icon: '✕',
    },
    warning: {
      background: `linear-gradient(180deg, ${theme.surfaces.card} 0%, ${theme.accents.warningSoft} 100%)`,
      border: `1px solid ${theme.accents.warningBorder}`,
      iconBackground: theme.accents.warning,
      icon: '⚠',
    },
    info: {
      background: `linear-gradient(180deg, ${theme.surfaces.card} 0%, ${theme.accents.infoSoft} 100%)`,
      border: `1px solid ${theme.accents.infoBorder}`,
      iconBackground: theme.accents.secondary,
      icon: 'ℹ',
    },
  };

  const style = typeStyles[toast.type] || typeStyles.info;

  return (
    <div
      onClick={handleClick}
      style={{
        ...style,
        borderRadius: '12px',
        padding: '12px 16px',
        color: theme.text.body,
        fontSize: '13px',
        fontWeight: '600',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        cursor: 'pointer',
        boxShadow: theme.shadows.card,
        backdropFilter: 'blur(8px)',
        transition: 'all 0.3s ease',
        opacity: isExiting ? 0 : 1,
        transform: isExiting ? 'translateX(100%)' : 'translateX(0)',
        maxWidth: '380px',
        wordBreak: 'break-word',
        lineHeight: 1.4,
      }}
    >
      <span style={{
        fontSize: '16px',
        fontWeight: '800',
        flexShrink: 0,
        width: '22px',
        height: '22px',
        borderRadius: '50%',
        background: style.iconBackground,
        color: theme.palette.white,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: `0 0 0 4px ${theme.surfaces.surface2}`,
      }}>
        {style.icon}
      </span>
      <span style={{ flex: 1 }}>{toast.message}</span>
    </div>
  );
};

// ─── Toast Container ───────────────────────────────────────────────────────
const ToastContainer = ({ toasts, onDismiss, themeKey }) => {
  if (toasts.length === 0) return null;

  return (
    <div style={{
      position: 'fixed',
      top: '16px',
      right: '16px',
      zIndex: 99999,
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      pointerEvents: 'none',
    }}>
      {toasts.map(toast => (
        <div key={toast.id} style={{ pointerEvents: 'auto' }}>
          <ToastItem toast={toast} onDismiss={onDismiss} themeKey={themeKey} />
        </div>
      ))}
    </div>
  );
};

// ─── useToast Hook ─────────────────────────────────────────────────────────
let toastIdCounter = 0;

export const useToast = () => {
  const [toasts, setToasts] = useState([]);
  const toastsRef = useRef([]);

  // Keep ref in sync for stable callback
  useEffect(() => {
    toastsRef.current = toasts;
  }, [toasts]);

  const addToast = useCallback((message, type = 'info', duration = 4000) => {
    const id = ++toastIdCounter;
    const newToast = { id, message, type, duration };
    setToasts(prev => [...prev.slice(-4), newToast]); // max 5 visible
    return id;
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const ToastRenderer = useCallback(({ themeKey } = {}) => (
    <ToastContainer toasts={toasts} onDismiss={dismissToast} themeKey={themeKey} />
  ), [toasts, dismissToast]);

  return { toasts, addToast, dismissToast, ToastContainer: ToastRenderer };
};
